import { Listbox, Transition } from "@headlessui/react";
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useMemo } from "react";
import { classNames } from "~/utils";

export const zoomLevels = [
  {
    label: "50%",
    value: 0.5,
  },
  {
    label: "75%",
    value: 0.75,
  },
  {
    label: "100%",
    value: 1,
  },
  {
    label: "125%",
    value: 1.25,
  },
  {
    label: "150%",
    value: 1.5,
  },
  {
    label: "200%",
    value: 2,
  },
  {
    label: "300%",
    value: 3,
  },
].map((elt, i, arr) => {
  const previous = i > 0 ? arr[i - 1].value : undefined;
  const next = i + 1 < arr.length ? arr[i + 1].value : undefined;
  return { ...elt, previous, next };
});
export function FloatingToolbar(props: {
  setZoomLevel: (zoomLevel: number) => void;
  zoomLevel: number;
}) {
  const currentZoomLevel = useMemo(
    () => zoomLevels.find((x) => x.value === props.zoomLevel) || zoomLevels[3],
    [props.zoomLevel],
  );
  return (
    <div
      className={"absolute top-8 left-[50%] -translate-y-1/2 -translate-x-1/2"}
    >
      <div
        className={
          "dark group bg-gray-800 opacity-[0.5] transition-opacity flex flex-row " +
          "rounded-full text-sm flex-wrap min-w-sm hover:opacity-100 flex flex-row " +
          "items-center justify-center space-x-2"
        }
      >
        <button
          className={"text-foreground disabled:text-muted-foreground"}
          disabled={!currentZoomLevel?.previous}
          onClick={() => {
            if (currentZoomLevel?.previous) {
              props.setZoomLevel(currentZoomLevel.previous);
            }
          }}
        >
          <MinusCircleIcon className={"h-6 h-6"} />
        </button>
        <div className={"relative"}>
          <Listbox
            value={currentZoomLevel}
            onChange={(x) => props.setZoomLevel(x.value)}
          >
            {({ open }) => (
              <>
                <Listbox.Button
                  className={"text-foreground disabled:text-muted-foreground"}
                >
                  {currentZoomLevel.label}
                </Listbox.Button>
                <Transition
                  show={open}
                  as={Fragment}
                  /* eslint-disable lingui/no-unlocalized-strings -- headlessui transition class names */
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  /* eslint-enable lingui/no-unlocalized-strings */
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 bg-gray-800 rounded-b-md -translate-x-[10%] text-sm">
                    {zoomLevels.map((zoom) => (
                      <Listbox.Option
                        key={zoom.value}
                        value={zoom}
                        className={({ active }) =>
                          classNames(
                            active
                              ? "text-foreground bg-primary"
                              : "text-muted-foreground",
                            "relative cursor-default select-none py-1 pl-2 pr-2",
                          )
                        }
                      >
                        {zoom.label}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </>
            )}
          </Listbox>
        </div>

        <button
          className={"text-foreground disabled:text-muted-foreground"}
          disabled={!currentZoomLevel?.next}
        >
          <PlusCircleIcon
            className={"h-6 h-6"}
            onClick={() => {
              if (currentZoomLevel?.next) {
                props.setZoomLevel(currentZoomLevel.next);
              }
            }}
          />
        </button>
      </div>
    </div>
  );
}
